export interface ValidationErrorMessage {
  type: string;
  error: string;
}

export class ValidationError extends Error {
  public readonly messages: ValidationErrorMessage[];

  public readonly isValidationError: boolean = true;

  constructor(messages: ValidationErrorMessage[] | string[]) {
    // If messages are strings, convert to ValidationErrorMessage format
    const formattedMessages: ValidationErrorMessage[] = messages.map((msg) => {
      if (typeof msg === 'string') {
        return { type: 'general', error: msg };
      }
      return msg;
    });

    super(formattedMessages.map((m) => m.error).join(', '));
    this.name = 'ValidationError';
    this.messages = formattedMessages;

    // Maintains proper stack trace for where our error was thrown (only available on V8)
    if (Error.captureStackTrace) {
      Error.captureStackTrace(this, ValidationError);
    }
  }

  /**
   * Get all error messages as an array of strings
   */
  getErrorMessages(): string[] {
    return this.messages.map((m) => m.error);
  }

  /**
   * Get errors filtered by type
   */
  getErrorsByType(type: string): string[] {
    return this.messages.filter((m) => m.type === type).map((m) => m.error);
  }

  /**
   * Check if there are any errors of a specific type
   */
  hasErrorType(type: string): boolean {
    return this.messages.some((m) => m.type === type);
  }
}

/**
 * Type guard to check if an error is a ValidationError
 */
export function isValidationError(error: any): error is ValidationError {
  return error && error.isValidationError === true && Array.isArray(error.messages);
}
